<script setup lang="ts">
import { ref, PropType, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { Participant, Project, TranslatedProduct } from "../../../types/PersologOnlineTypes.ts";
import { isValidEmail } from "../../../utils/GeneralUtils.ts";
import { ProfileAdministrationAPI } from "../../../api";
import { fetchData } from "../../../utils/ProductViewUtils.ts";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as () => Project,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
});

const emit = defineEmits(["update:valid", "update:participants", "update:duplicated"]);

const productOptions = ref<{ label: string; value: string; quantity: number }[]>([]);
const products = ref<TranslatedProduct[]>([]);
const participants = ref<Participant[] | undefined>(
    props.participants ? props.participants.map((participant) => ({ ...participant })) : undefined,
);

const email = ref<string>("");
const firstName = ref<string>("");
const lastName = ref<string>("");
const selectedProduct = ref<string>(props.project?.defaultProduct ?? props.project?.product);

const emailError = ref<boolean>(false);
const duplicationError = ref<boolean>(false);
const emptyMailError = ref<boolean>(false);

function isDuplicateEmail(emailToCheck: string): boolean {
    return participants.value?.some((p) => p.email === emailToCheck) ?? false;
}

function validateEmail(): boolean {
    emptyMailError.value = email.value === "";
    emailError.value = !emptyMailError.value && !isValidEmail(email.value);
    duplicationError.value = isDuplicateEmail(email.value);
    emit("update:duplicated", duplicationError.value);
    emit("update:valid", !emailError.value);
    return !emailError.value && !emptyMailError.value && !duplicationError.value;
}

function createEmptyParticipant(): Participant | null {
    if (!validateEmail()) {
        return null;
    }

    return {
        email: email.value,
        firstName: firstName.value || "",
        name: lastName.value || "",
        projectId: 0,
        selectedProduct: selectedProduct.value,
    };
}

async function fetchProducts() {
    const allProducts = await fetchData();
    products.value = allProducts.all.map(
        (product) => allProducts.purchased.find((p) => p.productNo === product.productNo) ?? product,
    );
}

function addParticipant() {
    const participant = createEmptyParticipant();
    if (participant && participants.value) {
        const existingParticipantIndex = participants.value.findIndex((p) => p.email === participant.email);
        if (existingParticipantIndex === -1) {
            participants.value.push(participant);
            emit("update:participants", participant);
            emit("update:valid", true);
        }
        email.value = "";
        firstName.value = "";
        lastName.value = "";
        selectedProduct.value = props.project?.defaultProduct ?? props.project?.product;
    }
}

defineExpose({
    addParticipant,
});

watch(
    () => props.participants,
    (newVal) => {
        participants.value = newVal ? newVal.map((participant) => ({ ...participant })) : undefined;
    },
    { immediate: true },
);
onMounted(async () => {
    await fetchProducts();
    const groupCharts = await ProfileAdministrationAPI.getBaseProductsForGroupChart(props.project?.product ?? "");
    if (groupCharts && groupCharts.length > 0) {
        groupCharts[0].products.products.map((product) => {
            const translatedProduct = products.value.find((p) => p.productNo === product.productNo);
            productOptions.value.push({
                label: translatedProduct?.productName ?? product.productNo,
                value: product.productNo,
                quantity: translatedProduct?.quantity ?? 0,
            });
        });
    }
});
</script>

<template>
    <div class="flex flex-wrap gap-4">
        <div class="basis-full md:basis-5/12 pl-md-0">
            <div class="flex flex-col gap-2">
                <InputText
                    id="participant-input_input_email"
                    class="w-full mr-md-1"
                    :placeholder="t('project-configuration.additionalRecipient.email')"
                    v-model="email"
                    @keyup.enter="addParticipant"
                    autocomplete="email"
                    @blur="validateEmail"
                    :class="{ 'p-invalid': emailError }"
                    type="email"
                />
                <small v-if="duplicationError" class="text-brand" id="participant-input_small_email">
                    {{ t("project-configuration.additionalRecipient.error.duplicated") }}
                </small>
                <small v-if="emailError" class="text-brand" id="participant-input_small_email">
                    {{ t("project-configuration.additionalRecipient.error.invalid") }}
                </small>
                <small v-if="emptyMailError" class="text-brand" id="participant-input_small_email">
                    {{ t("project-configuration.additionalRecipient.error.empty") }}
                </small>
            </div>
        </div>
        <div
            v-if="project && project.defaultProduct && project.product !== project.defaultProduct"
            class="basis-full md:flex-1 flex items-start"
        >
            <Select
                v-model="selectedProduct"
                :options="productOptions"
                option-label="label"
                option-value="value"
                :placeholder="t('project-details.defaultProduct.label-default')"
                filter
                id="participant-input_input_default-product"
                class="w-full mr-md-1"
            />
        </div>
        <div class="basis-full md:flex-1 flex items-start">
            <InputText
                id="participant-input_input_firstname"
                class="w-full mr-md-1"
                :placeholder="t('project-configuration.additionalRecipient.firstName')"
                v-model="firstName"
                @keyup.enter="addParticipant"
                autocomplete="off"
            />
        </div>
        <div class="basis-10/12 md:flex-1 flex items-start">
            <InputText
                id="participant-input_input_lastname"
                class="w-full mr-md-1"
                :placeholder="t('project-configuration.additionalRecipient.lastName')"
                v-model="lastName"
                @keyup.enter="addParticipant"
                autocomplete="off"
            />
        </div>
        <div class="basis-1/6 md:basis-auto md:flex-none flex items-start justify-end pr-md-0">
            <Button
                id="participant-input_btn_add-participant"
                icon="pi pi-plus"
                @click="addParticipant"
                :disabled="emailError || duplicationError"
                v-tooltip="t('project-details.participants.tooltips.add')"
            />
        </div>
    </div>
</template>
